/**
 * search-normalize.js
 *
 * Turns what a customer typed into the Shopify search query we actually run.
 * Pure logic, no DOM — `search-field-scope.js` is the glue that calls it.
 *
 * WHY THIS EXISTS
 * ---------------
 * Shopify's storefront search is a literal token matcher: no stemming, no
 * compound handling, no typo tolerance. Three spellings of one part return
 * three different result sets. Measured on the live store 2026-08-25, using
 * the field-scoped queries this theme builds:
 *
 *     camshaft   → 234     cam shaft  →  45     cam-shaft → 45
 *     camshafts  →  34     camshft    →   0
 *     handlebar  → 682     handlebars → 416     handle bar → 0
 *
 * Writing a synonym per product is not a realistic answer for a 37k-product
 * catalog. Instead we expand each typed word into the spellings the catalog
 * actually uses, checked against a vocabulary mined from the product titles
 * themselves (assets/search-lexicon.json, generated by the catalog-pipeline's
 * `export search-lexicon`). Four transforms, all lexicon-checked so we only
 * ever add spellings that exist in the catalog:
 *
 *     split    camchest   → cam + chest       (both halves are catalog words)
 *     join     cam shaft  → camshaft          (the join is a catalog word)
 *     plural   camshafts  → camshaft          (either form is a catalog word)
 *     typo     camshft    → camshaft          (edit distance 1)
 *
 * The scoping itself (title/tag/product_type/vendor instead of Shopify's
 * default everything-including-descriptions) is deliberate and unchanged —
 * see search-field-scope.js for that rationale.
 *
 * QUERY SHAPE
 * -----------
 * Every word becomes an OR over its spellings across the four scoped fields,
 * and the words are AND-ed together:
 *
 *     cam shaft
 *       → ((title:cam OR tag:cam OR product_type:cam OR vendor:cam)
 *          AND (title:shaft OR …))
 *         OR (title:camshaft OR tag:camshaft OR …)
 *
 * Note the fields are repeated per term rather than grouped as
 * `title:(camshaft OR camshafts)`. The grouped form looks tidier but silently
 * drops the field scoping — verified live, `title:(camshaft)` returns 528
 * results where `title:camshaft` returns 234, i.e. it falls back to matching
 * descriptions. Do not "simplify" it.
 *
 * DEGRADED MODE
 * -------------
 * Every entry point works without a lexicon (it may not have finished loading
 * when the customer hits enter). Lexicon-free, we still fix hyphens and try
 * the obvious plural, which is already better than today's exact-token match.
 */

(function (root, factory) {
  'use strict';
  var api = factory();
  /* Node (tests/search-normalize.test.js) and the browser share one file. */
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.SearchNormalize = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SCOPED_FIELDS = ['title', 'tag', 'product_type', 'vendor'];
  var SCOPE_REGEX = /\b(title|tag|product_type|vendor):/i;

  /* Guardrails. Each word costs (variants x 4 fields) clauses, so these caps
     are what keep the generated URL to roughly a kilobyte. */
  var MAX_WORDS = 6;
  var MAX_VARIANTS_PER_WORD = 3; /* including the word as typed */
  var MAX_SPLITS_PER_WORD = 1;
  /* Split halves get the word plus one plural — enough to catch "Turn
     Signals", cheap enough not to bloat the URL. */
  var MAX_SEQUENCE_VARIANTS = 2;
  var MAX_TYPO_FIXES = 2;
  var MAX_PAIR_JOINS = 2;

  /* Mirrors the miner's MIN_TOKEN_LEN: shorter halves split ambiguously. */
  var MIN_PIECE_LEN = 3;
  /* Below this a typo fix is guesswork — "cam" is one edit from a dozen words. */
  var MIN_TYPO_LEN = 4;
  /* Two edits is only safe once a word is long enough to stay distinctive. */
  var TYPO_DISTANCE_2_MIN_LEN = 7;

  /* ------------------------------------------------------------------ */
  /* Lexicon                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Wraps the generated lexicon: membership plus a rank (index in the vocab
   * array, which the miner emits most-common-first). Rank breaks ties between
   * competing splits and typo fixes in favour of the commoner word.
   */
  function Lexicon(data) {
    this.rank = Object.create(null);
    this.compounds = (data && data.compounds) || Object.create(null);
    var vocab = (data && data.vocab) || [];
    for (var i = 0; i < vocab.length; i++) {
      if (!(vocab[i] in this.rank)) this.rank[vocab[i]] = i;
    }
    this.vocab = vocab;
  }

  Lexicon.prototype.has = function (word) {
    return word in this.rank;
  };

  Lexicon.prototype.rankOf = function (word) {
    return word in this.rank ? this.rank[word] : Infinity;
  };

  /* ------------------------------------------------------------------ */
  /* Text handling                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * HTML entities ride in via pre-escaped q params (e.g. a predictive-search
   * link submitting "S&amp;S"). Decode so we scope the real term and the
   * restored input never shows "&amp;".
   */
  function decodeEntities(s) {
    return (s || '')
      .replace(/&amp;/gi, '&')
      .replace(/&#39;/g, "'")
      .replace(/&quot;/gi, '"')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>');
  }

  function isScoped(q) {
    return SCOPE_REGEX.test(q || '');
  }

  /**
   * Split a typed word into the tokens Shopify would index it as. A leading
   * "-" is Shopify's negation operator and a stray quote or paren corrupts the
   * whole query, so both are dropped rather than escaped.
   */
  function pieces(word) {
    var cleaned = String(word || '')
      .toLowerCase()
      .replace(/["'():\\]/g, '');
    return cleaned.split(/[^a-z0-9]+/).filter(Boolean);
  }

  function words(raw) {
    var trimmed = decodeEntities(String(raw || '')).trim();
    if (!trimmed) return [];
    return trimmed.split(/\s+/).slice(0, MAX_WORDS);
  }

  /* ------------------------------------------------------------------ */
  /* Spelling variants                                                   */
  /* ------------------------------------------------------------------ */

  /**
   * Candidate singular/plural forms of an English-ish word. Generated blind;
   * the caller keeps only the ones the catalog actually contains, which is
   * what stops "bus" → "bu" and similar nonsense reaching the query.
   */
  function inflections(word) {
    var out = [];
    if (word.length > 4 && /ies$/.test(word)) out.push(word.slice(0, -3) + 'y');
    if (word.length > 3 && /es$/.test(word)) out.push(word.slice(0, -2));
    if (word.length > 3 && /s$/.test(word)) out.push(word.slice(0, -1));
    if (!/s$/.test(word)) {
      out.push(word + 's');
      if (/y$/.test(word)) out.push(word.slice(0, -1) + 'ies');
      if (/(s|x|z|ch|sh)$/.test(word)) out.push(word + 'es');
    }
    return out;
  }

  /**
   * Levenshtein distance, abandoned as soon as it cannot come in at or under
   * `max`. The early exit is what makes scanning 4k vocabulary words per typed
   * word cheap enough to do synchronously on submit.
   */
  function editDistance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev = [];
    var i;
    for (i = 0; i <= b.length; i++) prev[i] = i;
    for (i = 1; i <= a.length; i++) {
      var curr = [i];
      var best = i;
      for (var j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (curr[j] < best) best = curr[j];
      }
      if (best > max) return max + 1;
      prev = curr;
    }
    return prev[b.length];
  }

  /**
   * Catalog words within one or two edits of a word we don't recognise.
   * Ranked by distance first, then by how common the candidate is.
   */
  function typoFixes(word, lexicon) {
    if (!lexicon || word.length < MIN_TYPO_LEN) return [];
    var max = word.length >= TYPO_DISTANCE_2_MIN_LEN ? 2 : 1;
    var found = [];
    for (var i = 0; i < lexicon.vocab.length; i++) {
      var candidate = lexicon.vocab[i];
      if (Math.abs(candidate.length - word.length) > max) continue;
      var d = editDistance(word, candidate, max);
      if (d <= max) found.push({ word: candidate, distance: d, rank: i });
    }
    found.sort(function (a, b) {
      return a.distance - b.distance || a.rank - b.rank;
    });
    return found.slice(0, MAX_TYPO_FIXES).map(function (f) {
      return f.word;
    });
  }

  /**
   * Ways to read a word as two catalog words ("camchest" → ["cam", "chest"]).
   * The mined compound map wins when it has an entry — it was built from pairs
   * attested in real titles, so it beats guessing.
   *
   * Candidates are gathered across *every* spelling of the word and then
   * ranked together, because the best split often comes from a variant rather
   * than what was typed: "camshafts" splits literally into cam + shafts, but
   * the singular's cam + shaft is what the catalog's titles actually say.
   */
  function splits(terms, lexicon) {
    if (!lexicon) return [];
    var candidates = [];
    var seen = Object.create(null);

    function add(parts, rank) {
      var key = parts.join(' ');
      if (key in seen) return;
      seen[key] = true;
      candidates.push({ parts: parts, rank: rank });
    }

    for (var t = 0; t < terms.length; t++) {
      var word = terms[t];
      var mapped = lexicon.compounds[word];
      if (mapped) {
        /* Attested in real titles — rank it ahead of anything derived. */
        add(mapped.split(' '), -1);
        continue;
      }
      for (var i = MIN_PIECE_LEN; i <= word.length - MIN_PIECE_LEN; i++) {
        var head = word.slice(0, i);
        var tail = word.slice(i);
        if (lexicon.has(head) && lexicon.has(tail)) {
          add([head, tail], lexicon.rankOf(head) + lexicon.rankOf(tail));
        }
      }
    }

    candidates.sort(function (a, b) {
      return a.rank - b.rank;
    });
    return candidates.slice(0, MAX_SPLITS_PER_WORD).map(function (c) {
      return c.parts;
    });
  }

  /**
   * Every spelling worth trying for one typed token.
   *
   * Returns `{ terms, splits }` — `terms` are single words OR-ed across the
   * scoped fields, `splits` are two-word readings that have to be AND-ed.
   */
  /** The token plus whichever of its singular/plural forms the catalog has. */
  function inflectionTerms(token, lexicon, max) {
    var terms = [token];
    var forms = inflections(token);
    for (var i = 0; i < forms.length && terms.length < max; i++) {
      var form = forms[i];
      /* Without a lexicon, trust only the plain -s/+s swap: it is the one
         inflection that is far more often right than wrong. */
      var acceptable = lexicon
        ? lexicon.has(form)
        : form === token + 's' || form === token.slice(0, -1);
      if (acceptable && terms.indexOf(form) === -1) terms.push(form);
    }
    return terms;
  }

  function variants(token, lexicon) {
    var terms = inflectionTerms(token, lexicon, MAX_VARIANTS_PER_WORD);
    var i;

    /* Only chase a typo when the word looks unknown and nothing else matched.
       A word that is in the catalog is not a typo, whatever else it resembles. */
    var unknown = lexicon && !lexicon.has(token);
    if (unknown && terms.length === 1) {
      var fixes = typoFixes(token, lexicon);
      for (i = 0; i < fixes.length; i++) {
        if (terms.indexOf(fixes[i]) === -1) terms.push(fixes[i]);
      }
    }

    var kept = terms.slice(0, MAX_VARIANTS_PER_WORD);
    return { terms: kept, splits: splits(kept, lexicon) };
  }

  /* ------------------------------------------------------------------ */
  /* Query building                                                      */
  /* ------------------------------------------------------------------ */

  /** `title:x OR tag:x OR product_type:x OR vendor:x` for each term. */
  function fieldClause(terms) {
    var clauses = [];
    for (var i = 0; i < terms.length; i++) {
      for (var f = 0; f < SCOPED_FIELDS.length; f++) {
        clauses.push(SCOPED_FIELDS[f] + ':' + terms[i]);
      }
    }
    return '(' + clauses.join(' OR ') + ')';
  }

  /**
   * A sequence of tokens that must all be present: `(…cam…) AND (…shaft…)`.
   *
   * Each token carries its own plural, because the catalog pluralises the
   * second half of a compound freely: "Turn Signals" outnumbers "Turn Signal".
   * Without this, typing "turnsignal" found 520 products where typing "turn
   * signal" found 862 — the same asymmetry this file exists to remove. No typo
   * correction here: these tokens came out of the vocabulary, so they're real
   * words by construction.
   */
  function sequenceClause(tokens, lexicon) {
    if (tokens.length === 1)
      return fieldClause(inflectionTerms(tokens[0], lexicon, MAX_SEQUENCE_VARIANTS));
    var parts = tokens.map(function (t) {
      return fieldClause(inflectionTerms(t, lexicon, MAX_SEQUENCE_VARIANTS));
    });
    return '(' + parts.join(' AND ') + ')';
  }

  /** Everything one typed word may match. */
  function wordClause(word, lexicon) {
    var tokens = pieces(word);
    if (!tokens.length) return null;

    /* Hyphenated or punctuated input ("cam-shaft") indexes as separate tokens,
       so match the sequence — and also the joined spelling, which is what the
       customer probably meant and what half the catalog uses. */
    if (tokens.length > 1) {
      var alternatives = [sequenceClause(tokens, lexicon)];
      var joined = tokens.join('');
      if (!lexicon || lexicon.has(joined)) {
        alternatives.push(fieldClause(variants(joined, lexicon).terms));
      }
      return alternatives.length === 1 ? alternatives[0] : '(' + alternatives.join(' OR ') + ')';
    }

    var v = variants(tokens[0], lexicon);
    var parts = [fieldClause(v.terms)];
    for (var i = 0; i < v.splits.length; i++) parts.push(sequenceClause(v.splits[i], lexicon));
    return parts.length === 1 ? parts[0] : '(' + parts.join(' OR ') + ')';
  }

  /**
   * Fold adjacent words whose joined spelling is a catalog word into a single
   * clause: "cam shaft" has to match "Camshaft Kit" too. Greedy, left to
   * right, capped — three-word joins are almost always coincidence.
   */
  function joinAdjacent(entries, lexicon) {
    if (!lexicon || entries.length < 2) return entries;
    var out = [];
    var joins = 0;
    for (var i = 0; i < entries.length; i++) {
      var a = entries[i];
      var b = entries[i + 1];
      var joined = b && joins < MAX_PAIR_JOINS ? a.token + b.token : null;
      if (joined && a.token && b.token && lexicon.has(joined)) {
        /* Spell the join out in full — its plurals matter as much as anything
           else's. Using the bare term here made "cam shaft" return 280 where
           "camshaft" returned 310: the 30 products titled "Camshafts". */
        out.push({
          token: joined,
          clause:
            '((' +
            a.clause +
            ' AND ' +
            b.clause +
            ') OR ' +
            fieldClause(variants(joined, lexicon).terms) +
            ')',
        });
        joins++;
        i++; /* b is consumed */
      } else {
        out.push(a);
      }
    }
    return out;
  }

  /**
   * The main entry point: typed text in, Shopify query out.
   *
   * Returns the input untouched when it is empty or already field-scoped, so
   * re-running it on a URL that has been through here once is a no-op.
   */
  function buildQuery(raw, lexicon) {
    var trimmed = decodeEntities(String(raw || '')).trim();
    if (!trimmed || isScoped(trimmed)) return trimmed;

    var entries = [];
    var list = words(trimmed);
    for (var i = 0; i < list.length; i++) {
      var clause = wordClause(list[i], lexicon);
      if (!clause) continue;
      var tokens = pieces(list[i]);
      entries.push({ token: tokens.length === 1 ? tokens[0] : '', clause: clause });
    }
    if (!entries.length) return '';

    entries = joinAdjacent(entries, lexicon);
    return entries
      .map(function (e) {
        return e.clause;
      })
      .join(' AND ');
  }

  /**
   * A broader query to try when the strict one found nothing.
   *
   * Multi-word: drop the rarest word — it is usually the one that's misspelt,
   * a colour, or a model year the title doesn't carry. Single word: give up on
   * scoping and hand the term to Shopify's native search, which will match
   * description text. That's noisy, but on a zero-result page noisy beats
   * empty. Returns null when there is nothing left to relax.
   */
  function relaxQuery(raw, lexicon) {
    var list = words(raw);
    if (!list.length) return null;

    if (list.length === 1) {
      var tokens = pieces(list[0]);
      return tokens.length ? { query: tokens.join(' '), scoped: false } : null;
    }

    var rarest = 0;
    var worst = -1;
    for (var i = 0; i < list.length; i++) {
      var token = pieces(list[i])[0] || '';
      var rank = lexicon ? lexicon.rankOf(token) : 0;
      /* Unknown words rank Infinity — exactly the ones worth dropping first. */
      if (rank > worst) {
        worst = rank;
        rarest = i;
      }
    }
    var kept = list.slice(0, rarest).concat(list.slice(rarest + 1));
    return { query: buildQuery(kept.join(' '), lexicon), scoped: true, dropped: list[rarest] };
  }

  return {
    Lexicon: Lexicon,
    buildQuery: buildQuery,
    relaxQuery: relaxQuery,
    decodeEntities: decodeEntities,
    isScoped: isScoped,
    /* Exported for tests and for reuse by the platform API's port. */
    _internals: {
      pieces: pieces,
      words: words,
      inflections: inflections,
      editDistance: editDistance,
      typoFixes: typoFixes,
      splits: splits,
      variants: variants,
      fieldClause: fieldClause,
    },
  };
});
